/*
 * File: WeSpeakAbout.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 9, 2025 at 5:09 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 4:55 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Components
import {
	Meta,
	AppNavigation,
	SchemaScript,
	Typography,
	LocaleLink,
	Padding,
} from '../../../../../src/components';
import { buildDynamicMeta } from '../../../../../src/components/Meta';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../../constants/base.constants';

// Styles
import * as S from './WeSpeakAbout.styles';


/**
 * Component
 */

const WeSpeakAbout = ({ meta: m }) => {

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageWeSpeakAbout.metaTitle', { partnerName: partner.name }),
		description: t('pageWeSpeakAbout.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'http://schema.org',
				'@type': ['WebPage', 'AboutPage'],
				'@id': `${partnerDomain}/about/#WebPage`,
				name: buildDynamicMeta(meta.title, { partner }),
				description: buildDynamicMeta(meta.description, { partner }),
				url: `${partnerDomain}${locale.localePath}/about`,
				inLanguage: locale.localeBaseId,
				isPartOf: {
					'@id': `${partnerDomain}/#WebSite`
				},
				publisher: {
					'@id': `${partner.website}/#Organization`,
				},
			}}
			/>

			{/* Component Content */}
			<AppNavigation showFooter contentPadding={false}>
				<S.Wrapper>

					{/* Hero */}
					<Padding left right>
						<S.PartnerHeroSection>
							<img
								src="https://d2vhy11spo2b40.cloudfront.net/drcc/we-speak-brand-icon.png"
								alt="We-Speak Logo"
								title="We-Speak Logo"
							/>
							<Typography tag="h1" weight="bold">About We-Speak</Typography>
						</S.PartnerHeroSection>
					</Padding>

					{/* What is We-Speak */}
					<Padding left right>
						<S.PartnerSection>
							<Typography tag="h2" weight="bold">What is We-Speak?</Typography>
							<Typography tag="p">
								We-Speak is a secure, anonymous online platform that aims to empower survivors of sexual violence to safely tell their own stories, in their own time, in their own words. The purpose of We-Speak is to provide a platform for these stories, in all of their diversity, so people can reclaim their voice.
							</Typography>
							<Typography tag="p">
								This space is an opportunity to anonymously write about your experience in your own words. It is for anyone who identifies as a victim, a survivor, a person who has experienced sexual violence of any kind, or for someone who is supporting another.
							</Typography>
							<Typography tag="p">
								We hope that these stories of struggle, strength, and healing can foster a community of support, solidarity, understanding and hope. We believe that stories are powerful and can build awareness, cultivate empathy, and ultimately create change. Every story matters.
							</Typography>
						</S.PartnerSection>
					</Padding>

					{/* Mantra Section */}
					<S.PartnerHighlightSection>
						<Padding left right>
							<S.PartnerHighlight>
								<Typography tag="h2" weight="bold" center>When survivors speak, we listen.</Typography>
							</S.PartnerHighlight>
						</Padding>
					</S.PartnerHighlightSection>

					{/* Why have we created We-Speak? */}
					<Padding left right>
						<S.PartnerSection>
							<Typography tag="h2" weight="bold">Why have we created We-Speak?</Typography>

							{/* Bullets */}
							<S.BulletContainer>

								{/* Empowerment */}
								<S.Bullet>
									<S.BulletIcon />
									<S.BulletContent>
										<Typography tag="h4" weight="bold">
											Empowerment
										</Typography>
										<Typography tag="p">
											Empowering survivors to share their experiences safely and anonymously, perhaps for the first time. Giving space for their real lived experiences to be seen and breaking the shame of silence.
										</Typography>
									</S.BulletContent>
								</S.Bullet>

								{/* Solidarity */}
								<S.Bullet>
									<S.BulletIcon />
									<S.BulletContent>
										<Typography tag="h4" weight="bold">
											Solidarity
										</Typography>
										<Typography tag="p">
											Allowing survivors to see that they are not alone by fostering a community of support, solidarity and hope for those who choose to post.
										</Typography>
									</S.BulletContent>
								</S.Bullet>

								{/* Awareness */}
								<S.Bullet>
									<S.BulletIcon />
									<S.BulletContent>
										<Typography tag="h4" weight="bold">
											Awareness
										</Typography>
										<Typography tag="p">
											Highlighting the prevalence, and impact, of sexual violence in Ireland and allowing those who do not have a lived experience to further their understanding of sexual violence, the many forms it takes and the long-lasting effects it can have.
										</Typography>
									</S.BulletContent>
								</S.Bullet>

								{/* Support */}
								<S.Bullet>
									<S.BulletIcon />
									<S.BulletContent>
										<Typography tag="h4" weight="bold">
											Support
										</Typography>
										<Typography tag="p">
											Providing survivors with trauma informed, tailored support and access to a hub of resources to promote healing.
										</Typography>
									</S.BulletContent>
								</S.Bullet>

							</S.BulletContainer>
						</S.PartnerSection>
					</Padding>

					{/* Contact Info */}
					<Padding left right>
						<S.PartnerContactSection>
							<Typography tag="h4" weight="bold">
								If you have any questions, please reach out to us at
								{' '}
								<LocaleLink title="info@we-consent.ie" aria-label="info@we-consent.ie" to="mailto:info@we-consent.ie?Subject=Question">info@we-consent.ie</LocaleLink>
								.
							</Typography>
						</S.PartnerContactSection>
					</Padding>

				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

WeSpeakAbout.propTypes = {
	meta: PropTypes.shape()
};
WeSpeakAbout.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default WeSpeakAbout;
